import { Controller } from '@hotwired/stimulus'

/**
 * A reusable Stimulus controller for creating tabbed interfaces. Handles tab
 * switching with proper active states and content panel visibility.
 *
 * @example Basic Usage
 * ```html
 * <div data-controller="tabs" data-tabs-default-tab-value="first">
 *   <!-- Tab Navigation -->
 *   <div class="sf-subnav sf-subnav-fluid">
 *     <div class="sf-subnav-inner">
 *       <div class="sf-subnav-item active"
 *            data-action="click->tabs#show"
 *            data-tabs-tab-param="first"
 *            data-tabs-target="tab">
 *         <span>First Tab</span>
 *       </div>
 *       <div class="sf-subnav-item"
 *            data-action="click->tabs#show"
 *            data-tabs-tab-param="second"
 *            data-tabs-target="tab">
 *         <span>Second Tab</span>
 *       </div>
 *     </div>
 *   </div>
 *
 *   <!-- Tab Content Panels -->
 *   <div data-tabs-target="panel" data-panel-type="first">
 *     <h3>First Tab Content</h3>
 *     <p>Content for the first tab goes here...</p>
 *   </div>
 *
 *   <div class="sf-hidden" data-tabs-target="panel" data-panel-type="second">
 *     <h3>Second Tab Content</h3>
 *     <p>Content for the second tab goes here...</p>
 *   </div>
 * </div>
 * ```
 *
 * TODO: This controller could be moved to be a part of the implementation of a
 * standard tabs view component. This would mean that the implementation comes
 * fully baked, along with the markup and the JavaScript, so future tab
 * implementations don't need to supply their own markup like in the example
 * above. Instead, just the content within the tab triggers and panels need to
 * be supplied.
 */
export default class extends Controller {
  static targets = ['tab', 'panel']
  static values = { defaultTab: String }

  declare defaultTabValue: string

  declare readonly tabTargets: HTMLElement[]
  declare readonly panelTargets: HTMLElement[]

  connect(): void {
    if (!this.defaultTabValue) return

    this.showTab(this.defaultTabValue)
  }

  show(event: Event & { params: { tab: string } }): void {
    this.showTab(event.params.tab)
  }

  private showTab(tab: string): void {
    if (!tab) return

    this.tabTargets.forEach((tabBtn) => {
      tabBtn.classList.toggle('active', tabBtn.dataset.tabsTabParam === tab)
    })

    this.panelTargets.forEach((panel) => {
      panel.classList.toggle('sf-hidden', panel.dataset.panelType !== tab)
    })
  }
}
